import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";
import styles from "./FeedbackModal.module.css";
import { getLeadID } from "../../utils/storage";
import ChatSupportAPI from "../../services/chat-support-api";
import {
  clevertap_bot_popup_screen,
  clevertap_bot_popup_screen_click,
  clevertap_feedback_submitted,
} from "../../lib/clevertap-events";

const chatSupportAPI = new ChatSupportAPI();

// getLeadID() returns Number(localStorage value), so a missing leadID comes
// back as 0 (or NaN if malformed); send null instead of an invalid lead.
function getFeedbackLeadID() {
  return getLeadID() || null;
}

async function postFeedback(payload) {
  try {
    await chatSupportAPI.chatbotFeedbackAPI(payload);
    return true;
  } catch (error) {
    console.error("[FeedbackModal] submit failed", error);
    return false;
  }
}

export default function FeedbackModal({ onDone }) {
  const { t } = useTranslation();
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    clevertap_bot_popup_screen();
  }, []);

  function handleSkip() {
    clevertap_bot_popup_screen_click("Cross");
    // Fire-and-forget: postFeedback catches its own errors and never rejects.
    void postFeedback({ leadID: getFeedbackLeadID(), status: "SKIPPED" });
    onDone();
  }

  async function handleSubmit() {
    if (!rating || submitting) return;
    clevertap_bot_popup_screen_click("Submit");
    setSubmitting(true);
    const success = await postFeedback({
      leadID: getFeedbackLeadID(),
      status: "SUBMITTED",
      rating,
      comments: comment,
    });
    setSubmitting(false);
    if (!success) {
      Swal.fire({ icon: "error", text: t("feedbackModal.text_9") });
      return;
    }
    clevertap_feedback_submitted({ rating, response: comment });
    onDone();
  }

  const activeRating = hoverRating || rating;

  return (
    <div className={styles.overlay}>
      <div
        className={styles.modal}
        role="dialog"
        aria-label={t("feedbackModal.text_1")}
      >
        <button
          type="button"
          className={styles.closeBtn}
          onClick={handleSkip}
          aria-label={t("feedbackModal.text_2")}
        >
          ×
        </button>

        <h2 className={styles.title}>{t("feedbackModal.text_1")}</h2>
        <p className={styles.subtitle}>{t("feedbackModal.text_3")}</p>

        <div className={styles.stars} role="radiogroup"
          aria-label={t("feedbackModal.text_4")}
        >
          {[1, 2, 3, 4, 5].map((star) => (
            <button
              type="button"
              key={star}
              className={styles.starBtn}
              onClick={() => setRating(star)}
              onMouseEnter={() => setHoverRating(star)}
              onMouseLeave={() => setHoverRating(0)}
              role="radio"
              aria-checked={rating === star}
              aria-label={t("feedbackModal.text_5", { count: star })}
            >
              {star <= activeRating ? "★" : "☆"}
            </button>
          ))}
        </div>

        <textarea
          className={styles.textarea}
          placeholder={t("feedbackModal.text_6")}
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          rows={3}
        />

        <button
          type="button"
          className={styles.submitBtn}
          onClick={handleSubmit}
          disabled={!rating || submitting}
        >
          {submitting ? t("feedbackModal.text_7") : t("feedbackModal.text_8")}
        </button>
      </div>
    </div>
  );
}
